<template>
    <Attr :widget_id="widget_id" :attr_id="attr_id" :initialAttr="attr">
        <div
            :class="'attrs ' + attr['cssClass']"
            v-if="attr && (attr.visible !== undefined ? attr.visible : true)"
            :style="attr.cssStyle"
        >
            <div
                :class="['label-tab', attr.header_cssClass]"
                v-if="parent_type !== 'AttrTabPanel' && attr.title && !attr.hideTitle"
                :for="attr.html_id"
            >
                {{ attr.title }}
            </div>
            <component v-for="attrC in filteredAttrs"
                :widget_id="widget_id"
                :key="attrC.html_id"
                :is="graphEngine_component(store, WIDGET, attrC)"
                :attr_id="attrC.id"
                v-bind="childParentAttr(attrC) ? { parentAttr: childParentAttr(attrC) } : {}"
            />
            <!-- IE 2026 09 28 / ODOO-5800: вкладка-заглушка каркаса формы настроек — содержимое догружается (store.settingsForm_ensure); не пришло — повтор по клику -->
            <div v-if="attr.lazyForm" class="sfl-tab-loading" @click="sflRetry">
                <i v-if="WIDGET?._sflState !== 'error'" class="pi pi-spin pi-spinner"></i>
                <span>{{ WIDGET?._sflState === 'error' ? t('Could not load the settings form — click to retry') : t('Loading…') }}</span>
            </div>
            <!-- IE 2026 09 26 / ODOO-341407: плашка неполной выборки списка на вкладке-визуализации (Гант движений, график, карта…): островок у нижнего края
                 вкладки поверх содержимого — якорь нулевой высоты, вёрстка и расчёт высот визуализаций не меняются. Грид списка обрезку показывает чипсом
                 страниц (CHIP-Limit), вкладки с гридом плашку не несут. Крестик прячет её до следующей выборки с другим объёмом / страницей -->
            <div v-if="limitNotice" class="list-limit-anchor">
                <div class="list-limit-island" role="status" v-wjTooltipFn="limitNotice_tip">
                    <i class="pi pi-exclamation-triangle list-limit-icon"></i>
                    <span class="list-limit-text"><b>{{ limitNotice.text }}</b> — {{ limitNotice.hint }}</span>
                    <i class="pi pi-times list-limit-close" :title="t('Hide')" @click.stop="limitNotice_hide"></i>
                </div>
            </div>
        </div>
    </Attr>
</template>

<script>
import { useMainStore } from '@/stores/mainStore'
import { listLimitInfo, listLimitSig } from '@/utils/listLimit'   // IE 2026 09 26 / ODOO-341407: плашка неполной выборки списка
import { getUiLocale } from '@/i18n'   // IE 2026 09 26 / ODOO-341407: разряды чисел плашки — по языку интерфейса
import { graphEngine_component } from './graph/lens/lensSwitch'   // Odoo 348733: граф DataHub — движок «линза» опцией виджета (viewParams.<attr>.graphEngine = 'lens'), без опции — AttrGraph

export default {
    props: {
        widget_id: [String, Number],
        attr_id: String,
        parent_type: String,
        initialAttr: {
            type: Object,
            default: null,
        },
        parentAttr: {
            type: Object,
            default: null,
        },
    },
    data: () => ({
        WIDGET: {},
        attr: null,        // в data — реактивная смена объекта вкладки (initialAttr)
        parent_attr: null,
    }),
    setup() {
        const store = useMainStore();
        return { store }
    },
    created() {
        // console.log(`created ${this.attr_id}`)
        // виджета может ещё не быть в сторе (стол пересоздаётся при смене процессной
        // модели) — WIDGET остаётся null, widget_attr_set перерезолвит его по id;
        // жёсткое чтение WORKSPACE тут роняло рендер всего поддерева вкладок
        const WIDGET = this.WIDGET = this.store.findWidget(this.widget_id).WIDGET
        const WORKSPACE = WIDGET && this.store.WORKSPACES[WIDGET.workspace]
        this.PAGE = WORKSPACE?.PAGES?.[WIDGET?.page]
        this.content_Changed()
    },
    methods: {
        graphEngine_component,   // Odoo 348733
        content_Changed() {
            if (this.initialAttr && (this.store.tabs_noId || this.attr_id == null)) {
                // вкладка передана объектом (tabs_noId) — поиск по id не нужен
                this.attr = this.initialAttr
                this.parent_attr = this.parentAttr
            } else {
                this.store.widget_attr_set(this)
            }
        },
        childParentAttr(attrC) {
            // структурный родитель — только компонентам, пишущим hide/countItems вкладки
            return ['AttrGrid', 'AttrTabGrid', 'AttrStrMulti', 'AttrMD'].includes(attrC.component) ? this.attr : null
        },
        // IE 2026 09 28 / ODOO-5800: повтор догрузки формы настроек после ошибки
        sflRetry() {
            if (this.WIDGET?._sflState === 'error') this.store.settingsForm_ensure(this.WIDGET, 'retry')
        },
        // IE 2026 09 26 / ODOO-341407: подробность плашки неполной выборки — подсказка платформы (Alt / Show tooltips), контент в момент показа
        limitNotice_tip() {
            const n = this.limitNotice
            return n ? n.tip : ''
        },
        // IE 2026 09 26 / ODOO-341407: крестик — плашка прячется до выборки с другим отпечатком (страница / объём); отметка живёт на виджете, не в записи
        limitNotice_hide() {
            const n = this.limitNotice
            if (n && this.WIDGET) this.WIDGET._limitNoticeHidden = n.sig
        },
    },
    computed: {
        filteredAttrs() {
            if (this.attr?.attrs) {
                return this.attr.attrs.filter(attr => attr.use !== false)
            } else {
                return []
            }
        },
        // IE 2026 09 26 / ODOO-341407: вкладка — визуализация строк списка (дочерний не-грид на _doc.data: Гант движений, график, пузырьки, карта, сравнение, TreeMap)
        limitHost() {
            return this.filteredAttrs.some(c => c && c.binding === '_doc.data' && c.component !== 'AttrGrid')
        },
        // IE 2026 09 26 / ODOO-341407: плашка «показаны первые N из M» — признак бэка record.doc._limit_truncated (utils/listLimit.js); строки UI — английские, переводы в словарях
        limitNotice() {
            if (!this.limitHost) return null
            const info = listLimitInfo(this.WIDGET?.record)
            if (!info) return null
            const sig = listLimitSig(info)
            if (this.WIDGET?._limitNoticeHidden === sig) return null
            const loc = getUiLocale()
            const f = (n) => Number(n).toLocaleString(loc)
            let text
            if (info.offset > 0) text = this.t('Showing rows {from}–{to} of {total}', { from: f(info.offset + 1), to: f(info.offset + info.rows), total: info.total == null ? '?' : f(info.total) })
            else if (info.total != null) text = this.tn(info.total, 'Showing the first {first} of {n} row', 'Showing the first {first} of {n} rows', { first: f(info.rows), n: f(info.total) })
            else text = this.tn(info.rows, 'Showing only the first {n} row', 'Showing only the first {n} rows', { n: f(info.rows) })
            const tip = info.by === 'dataLimit'
                ? this.t('The data size limit of the query cut the list at {n} rows; this view is built from the loaded rows only', { n: f(info.limit) })
                : this.t('The list loads at most {n} rows (the row limit of the widget or the database setting “Row limit by default”); this view is built from the loaded rows only', { n: f(info.limit) })
            return { text, hint: this.t('narrow the selection (selectors, filters) to see all'), tip, sig }
        },
    },
    watch: {
        initialAttr(attr) {
            // пересборка view заменила объект вкладки — подхватываем без поиска
            if (attr && (this.store.tabs_noId || this.attr_id == null)) {
                this.attr = attr
                this.parent_attr = this.parentAttr
            }
        },
    },
}
</script>

<style>
.attrs {
    width:-webkit-fill-available;
    display: flex;
    flex-direction: column;
    /* flex-wrap: wrap; */
}
.label-attr {
    font-size: 0.875rem;
    top: 4px;
    color: #3f51b5;
    margin-left: 20px;
}
.label-tab {
    font-size: 0.875rem;
    text-transform: uppercase;
    font-weight: bold;
    border-bottom: 2px solid #77889923;
    top: 4px;
    color: #778899;
    padding: 8px 12px 8px 12px;
    margin-bottom: 8px;
}
.header-1 {
    background: #f2f5f5;
}
.widget-command-panel-2 {
    position: absolute;
    z-index: 110;
    top: 40px;
    right: 10px;
    width: fit-content !important;
}
.widget-command-panel-2 .wj-widget-control {
    background: #f2f5f5 !important;
}
/* IE 2026 09 26 / ODOO-341407: плашка неполной выборки списка — якорь нулевой высоты в конце вкладки, островок над нижним краем по центру
   (левый низ занимает плеер Flow Map, правый — масштаб пузырькового графика); вид островков платформы (.corner-island) + янтарная кромка */
/* IE 2026 09 28 / ODOO-5800: вкладка-заглушка каркаса формы настроек — пока содержимое догружается */
.sfl-tab-loading { display: flex; align-items: center; gap: 8px; padding: 16px; color: #778899; font-size: 0.875rem; cursor: default; }
.list-limit-anchor { position: relative; height: 0; flex: none; z-index: 12; }
.list-limit-island {
    position: absolute; bottom: 8px; left: 50%; transform: translateX(-50%);
    display: inline-flex; align-items: center; gap: 6px; max-width: calc(100% - 24px); box-sizing: border-box; padding: 3px 8px;
    background: rgba(255, 251, 235, 0.94); -webkit-backdrop-filter: blur(4px); backdrop-filter: blur(4px);
    border: 1px solid rgba(217, 119, 6, 0.45); border-radius: 10px; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    color: #78350f; font-size: 11px; line-height: 16px; white-space: nowrap; pointer-events: auto;
}
.list-limit-island .list-limit-icon { color: #d97706; font-size: 12px; flex: none; }
.list-limit-island .list-limit-text { overflow: hidden; text-overflow: ellipsis; min-width: 0; }
.list-limit-island .list-limit-text b { font-weight: 600; }
.list-limit-island .list-limit-close { flex: none; font-size: 10px; color: #92400e; cursor: pointer; padding: 2px; border-radius: 4px; }
.list-limit-island .list-limit-close:hover { background: rgba(217, 119, 6, 0.15); color: #451a03; }
</style>